/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBeneficialOwnership,
  runAndPersistAdverseMedia,
  IRunAdverseMediaParams,
} from './ai.service';
import { entityKeys } from '../entities/entities.hooks';

// Query key constants
const QUERY_KEYS = {
  AI: 'ai',
  BENEFICIAL_OWNERSHIP: 'beneficial-ownership',
} as const;

// Query key factories
const aiKeys = {
  all: () => [ QUERY_KEYS.AI ] as const,
  beneficialOwnership: () =>
    [ ...aiKeys.all(), QUERY_KEYS.BENEFICIAL_OWNERSHIP ] as const,
  beneficialOwnershipByEntity: (
    entityName: string,
    country?: string,
    address?: string,
  ) =>
    [ ...aiKeys.beneficialOwnership(), entityName, country, address ] as const,
};

export const useGetBeneficialOwnership = (
  entityName?: string,
  country?: string,
  address?: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: aiKeys.beneficialOwnershipByEntity(
      entityName || '',
      country,
      address,
    ),
    queryFn: () => getBeneficialOwnership(entityName!, country, address),
    enabled: enabled && Boolean(entityName),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useRunBeneficialOwnershipSearch = () => {
  return useMutation({
    mutationFn: ({
      entityName,
      country,
      address,
      records,
      potentialMatchCount,
    }: {
      entityName: string;
      country?: string;
      address?: string;
      records?: any[];
      potentialMatchCount?: number;
    }) =>
      getBeneficialOwnership(
        entityName,
        country,
        address,
        records,
        potentialMatchCount,
      ),
  });
};

/**
 * Runs the adverse media check on the entity service, persisting the result to
 * DatabaseEntity. This is the ONLY adverse-media path: there is deliberately no
 * client-side fallback to pyapi /screening/osint, because the entity service
 * already performs that search and a fallback would bill it a second time.
 * Invalidates entity-by-id (and the database entity list when known) so
 * adverse_media_info refreshes without blasting unrelated entity queries.
 */
export const useRunAndPersistAdverseMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: IRunAdverseMediaParams) => runAndPersistAdverseMedia(params),
    onSuccess: (_, variables) => {
      // Match useEvaluateOwnership: refresh by-id entity payloads (includes
      // Supplier page + Results' own useGetEntityById).
      queryClient.invalidateQueries({ queryKey: entityKeys.entity() });
      if (variables.databaseId) {
        queryClient.invalidateQueries({
          queryKey: entityKeys.entitiesByDatabasePrefix(variables.databaseId),
        });
      }
    },
  });
};
